import { useEffect, useState } from 'react';
import {
  isAdmin,
  type AdAttributeMappingDto,
  type AdSettingsDto,
  type AdStatusDto,
} from '@ciimar/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useT } from '../lib/i18n';

export function ActiveDirectory() {
  const t = useT();
  const { user } = useAuth();
  const admin = isAdmin(user?.roles ?? []);
  const [status, setStatus] = useState<AdStatusDto | null>(null);
  const [settings, setSettings] = useState<AdSettingsDto | null>(null);
  const [patternsText, setPatternsText] = useState('');
  const [mappings, setMappings] = useState<AdAttributeMappingDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  // New-mapping form
  const [fieldKey, setFieldKey] = useState('');
  const [adAttribute, setAdAttribute] = useState('');

  // Username tester
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suggestion, setSuggestion] = useState<string | null | undefined>(undefined);

  async function load() {
    setError(null);
    try {
      const [st, se, mp] = await Promise.all([api.adStatus(), api.adSettings(), api.adMappings()]);
      setStatus(st);
      setSettings(se);
      setPatternsText(se.usernamePatterns.join('\n'));
      setMappings(mp);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    if (admin) void load();
  }, [admin]);

  async function saveSettings(e: React.FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api.updateAdSettings({
        defaultOu: settings.defaultOu,
        upnSuffix: settings.upnSuffix,
        usernamePatterns: patternsText
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setSettings(updated);
      setPatternsText(updated.usernamePatterns.join('\n'));
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addMapping(e: React.FormEvent) {
    e.preventDefault();
    if (!fieldKey.trim() || !adAttribute.trim()) return;
    setError(null);
    try {
      await api.createAdMapping({ fieldKey: fieldKey.trim(), adAttribute: adAttribute.trim() });
      setFieldKey('');
      setAdAttribute('');
      setMappings(await api.adMappings());
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function removeMapping(id: string) {
    await api.deleteAdMapping(id);
    setMappings(await api.adMappings());
  }

  async function testUsername(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await api.suggestUsername(firstName, lastName);
      setSuggestion(res.username);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!admin) {
    return (
      <section>
        <div className="page-head">
          <h2>{t('admin.ad.title')}</h2>
        </div>
        <p className="muted">{t('admin.ad.adminOnly')}</p>
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('admin.ad.title')}</h2>
          <p className="page-sub">{t('admin.ad.subtitle')}</p>
        </div>
      </div>
      <p className="muted">{t('admin.ad.intro')}</p>

      {error && <div className="alert error">{error}</div>}
      {saved && <div className="alert ok">{t('admin.ad.saved')}</div>}

      <h3 style={{ marginTop: 8 }}>{t('admin.ad.statusTitle')}</h3>
      <div className="field-grid" style={{ marginTop: 8 }}>
        <div className="field">
          <dt>{t('admin.ad.mode')}</dt>
          <dd>{status ? status.mode.toUpperCase() : '…'}</dd>
        </div>
        <div className="field">
          <dt>{t('admin.ad.configured')}</dt>
          <dd>
            {status ? (
              <span className={`badge ${status.configured ? 'approved' : 'changes'}`}>
                {status.configured ? t('admin.ad.yes') : t('admin.ad.no')}
              </span>
            ) : (
              '…'
            )}
          </dd>
        </div>
        <div className="field">
          <dt>{t('admin.ad.url')}</dt>
          <dd>{status?.url || '—'}</dd>
        </div>
      </div>

      {settings && (
        <form className="add-field-form" onSubmit={saveSettings} style={{ marginTop: 16 }}>
          <h3>{t('admin.ad.settingsTitle')}</h3>
          <label>
            {t('admin.ad.defaultOu')}
            <input
              value={settings.defaultOu}
              onChange={(e) => setSettings({ ...settings, defaultOu: e.target.value })}
              placeholder="OU=Novos,OU=Colaboradores,DC=ciimar,DC=local"
            />
          </label>
          <label>
            {t('admin.ad.upnSuffix')}
            <input
              value={settings.upnSuffix}
              onChange={(e) => setSettings({ ...settings, upnSuffix: e.target.value })}
              placeholder="ciimar.up.pt"
            />
          </label>
          <label>
            {t('admin.ad.patternsLabel')}
            <textarea
              rows={3}
              value={patternsText}
              onChange={(e) => setPatternsText(e.target.value)}
              placeholder={'{f}{last}\n{first}.{last}'}
            />
          </label>
          <div className="actions-row">
            <span />
            <button type="submit" disabled={busy}>
              {busy ? t('admin.ad.saveBusy') : t('admin.ad.save')}
            </button>
          </div>
        </form>
      )}

      <h3 style={{ marginTop: 24 }}>{t('admin.ad.mappingTitle')}</h3>
      <form className="filters" onSubmit={addMapping} style={{ marginBottom: 12 }}>
        <input
          placeholder={t('admin.ad.fieldKeyPlaceholder')}
          value={fieldKey}
          onChange={(e) => setFieldKey(e.target.value)}
        />
        <input
          placeholder={t('admin.ad.adAttributePlaceholder')}
          value={adAttribute}
          onChange={(e) => setAdAttribute(e.target.value)}
        />
        <button type="submit">{t('admin.ad.add')}</button>
      </form>
      {mappings.length === 0 ? (
        <p className="muted">{t('admin.ad.noMappings')}</p>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>{t('admin.ad.colFieldKey')}</th>
              <th>{t('admin.ad.colAdAttribute')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {mappings.map((m) => (
              <tr key={m.id}>
                <td>{m.fieldKey}</td>
                <td>{m.adAttribute}</td>
                <td>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => void removeMapping(m.id)}
                  >
                    {t('admin.ad.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 style={{ marginTop: 24 }}>{t('admin.ad.test.title')}</h3>
      <form className="filters" onSubmit={testUsername}>
        <input
          placeholder={t('admin.ad.test.firstName')}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <input
          placeholder={t('admin.ad.test.lastName')}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <button type="submit" className="secondary">
          {t('admin.ad.test.submit')}
        </button>
      </form>
      {suggestion !== undefined && (
        <p style={{ marginTop: 10 }}>
          {suggestion ? (
            <>
              {t('admin.ad.test.resultLabel')}
              <strong>{suggestion}</strong>
            </>
          ) : (
            <span className="muted">{t('admin.ad.test.noResult')}</span>
          )}
        </p>
      )}
    </section>
  );
}
